import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Formik, Form } from 'formik';
import { Grid, Typography, TextField, Button as MUIButton } from '@material-ui/core';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';

import Modal from 'components/Modal';
import Loader from 'components/Loader';
import Input from 'components/Input';
import Status from 'components/Status';
import Button from 'components/Button';
import PasswordUpdate from 'components/PasswordUpdate';

import { setEmptyEmployee } from 'pages/EmployeesRegistration/store/actions';
import { updateEmployeePassword } from 'pages/EmployeesRegistration/store/thunk';
import { upsertSelfServiceUser } from 'pages/SelfService/store/thunk';

import { getPasswordStrength, getPasswordRules } from 'components/PasswordUpdate/resources';

import { formatAvailableEstablishmentsToUuid } from 'helpers/formatters';
import useWindowDimensions from 'helpers/hooks/useWindowDimensions';

import { validations } from './validations';

import { useStyles } from './styles';

export default function SelfServiceUserRegistration({ open, onClose, page = 1 }) {
  const dispatch = useDispatch();

  const classes = useStyles();

  const { width } = useWindowDimensions();
  const isSm = width < 960;

  const { userId } = useSelector(state => state.profile);
  const { isAdminUser } = useSelector(state => state.profile.submit);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const establishments = useSelector(state => state.login.establishments);
  const { employeeId, submit, isLoading: isEmployeeLoading } = useSelector(state => state.employeesRegistration);
  const { isLoading: isSubmitting } = useSelector(state => state.selfService);

  const isUserAdmin = +isAdminUser == 1;
  const isEditing = !!employeeId;

  const [isPasswordUpdateOpen, setIsPasswordUpdateOpen] = useState(false);
  const [isPasswordUpdateLoading, setIsPasswordUpdateLoading] = useState(false);

  const availableEstablishments = formatAvailableEstablishmentsToUuid(establishments)
    ?.filter(establishment => establishment?.establishmentTypes?.includes(1) || establishment?.establishmentTypes?.includes('1'))
    ?.map(establishment => {
      return {
        ...establishment,
        label: isUserAdmin ? `${establishment.establishmentId} - ${establishment.label}` : establishment.label
      }
    });

  const editingEstablishmentId = submit?.establishments?.[0];
  const editingEstablishmentUuid = establishments?.find(establishment => establishment?.establishmentId == editingEstablishmentId)?.uuid;

  const filterOptions = (options, state) => {
    const OPTIONS_LIMIT = 500;
    const defaultFilterOptions = createFilterOptions();

    return defaultFilterOptions(options, state).slice(0, OPTIONS_LIMIT);
  }

  const initialValues = {
    employeeId,
    status: isEditing ? submit?.status?.toString() : '0',
    name: submit?.name || '',
    email: submit?.email || '',
    establishmentUuid: editingEstablishmentUuid || '',
    password: '',
    passwordConfirm: ''
  }

  const handleClose = () => {
    dispatch(setEmptyEmployee());
    onClose();
  }

  const handleSubmit = values => {
    const params = { ...values }

    params['establishments'] = [params['establishmentUuid']];

    delete params['employeeId'];
    delete params['status'];
    delete params['passwordConfirm'];
    delete params['establishmentUuid'];

    if(isEditing) {
      delete params['password'];
    }

    dispatch(upsertSelfServiceUser({ userId, establishmentId, params, editingUserId: employeeId, page }))
      .then(() => handleClose());
  }

  return(
    <Modal
      id='self-service-user-registration-modal'
      title={isEditing ? 'Editar Autoatendimento' : 'Adicionar Autoatendimento'}
      maxWidth='md'
      open={open}
      onClose={handleClose}
    >
      {isPasswordUpdateOpen && (
        <PasswordUpdate
          isDismissable
          isEmployeeEdit
          isLoading={isPasswordUpdateLoading}
          isOpen={isPasswordUpdateOpen}
          setIsOpen={setIsPasswordUpdateOpen}
          hasLogoutWarning={false}
          onSuccess={values => {
            setIsPasswordUpdateLoading(true);

            dispatch(updateEmployeePassword(userId, employeeId, values))
              .then(() => setIsPasswordUpdateOpen(false))
              .finally(() => setIsPasswordUpdateLoading(false));
          }}
        />
      )}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validations}
      >
        {({ ...formikProps }) => {
          return(
            <Form>
              <Loader isLoading={isEmployeeLoading || isSubmitting}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography color='primary' variant='body2'>
                      <b>Nome</b>
                    </Typography>
                    <Input
                      id='name'
                      name='name'
                      type='text'
                      label='Digite o nome'
                      value={formikProps.values.name}
                      error={!!formikProps.errors.name}
                      helperText={formikProps.errors.name}
                      onChange={formikProps.handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography color='primary' variant='body2'>
                      <b>Status</b>
                    </Typography>
                    <Status
                      disabled
                      name='status'
                      value={formikProps.values.status}
                      onChange={formikProps.handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography color='primary' variant='body2'>
                      <b>E-mail</b>
                    </Typography>
                    <Input
                      id='email'
                      name='email'
                      type='text'
                      label='Digite o e-mail'
                      value={formikProps.values.email}
                      error={!!formikProps.errors.email}
                      helperText={formikProps.errors.email}
                      onChange={formikProps.handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography color='primary' variant='body2'>
                      <b>Estabelecimento</b>
                    </Typography>
                    <Autocomplete
                      id='establishmentUuid'
                      classes={{ root: classes.root }}
                      options={availableEstablishments}
                      filterOptions={filterOptions}
                      value={availableEstablishments?.find(establishment => establishment.value == formikProps.values.establishmentUuid) || null}
                      getOptionLabel={option => option.label}
                      getOptionSelected={(option, value) => option.value == value.value}
                      onChange={(_, establishment) => formikProps.setFieldValue('establishmentUuid', establishment?.value || '')}
                      renderInput={params => (
                        <TextField
                          {...params}
                          variant='outlined'
                          color='primary'
                          label='Selecione o estabelecimento'
                          error={!!formikProps.errors.establishmentUuid}
                          helperText={formikProps.errors.establishmentUuid}
                        />
                      )}
                    />
                  </Grid>
                  {!isEditing && (
                    <>
                      <Grid item xs={12} md={6}>
                        <Typography color='primary' variant='body2'>
                          <b>Senha</b>
                        </Typography>
                        <Input
                          id='password'
                          name='password'
                          type='password'
                          label='Digite a senha'
                          value={formikProps.values.password}
                          error={!!formikProps.errors.password}
                          helperText={formikProps.errors.password}
                          onChange={formikProps.handleChange}
                        />
                        <div style={{ marginTop: 10, marginBottom: 10 }}>
                          {getPasswordStrength(formikProps?.values?.password || '')}
                        </div>
                        <div style={{ marginTop: 10, marginBottom: 10 }}>
                          {getPasswordRules(formikProps?.values?.password || '')}
                        </div>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography color='primary' variant='body2'>
                          <b>Confirmar senha</b>
                        </Typography>
                        <Input
                          id='passwordConfirm'
                          name='passwordConfirm'
                          type='password'
                          label='Confirme a senha'
                          value={formikProps.values.passwordConfirm}
                          error={!!formikProps.errors.passwordConfirm}
                          helperText={formikProps.errors.passwordConfirm}
                          onChange={formikProps.handleChange}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} style={{ display: 'flex', flexDirection: isSm ? 'column' : 'row', justifyContent: 'flex-end', gap: 10 }}>
                    {isEditing && (
                      <Button
                        fullWidth={isSm}
                        type="button"
                        color="primary"
                        onClick={() => setIsPasswordUpdateOpen(true)}
                      >
                        Alterar Senha
                      </Button>
                    )}
                    <MUIButton
                      fullWidth={isSm}
                      type='button'
                      color='primary'
                      variant='outlined'
                      onClick={handleClose}
                    >
                      Cancelar
                    </MUIButton>
                    <Button
                      fullWidth={isSm}
                      type='submit'
                      color='success'
                    >
                      Salvar
                    </Button>
                  </Grid>
                </Grid>
              </Loader>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
}